import React, { useEffect, useState, useRef, useMemo } from 'react';
import ReactMapboxGL from 'react-mapbox-gl';
import ReactMapsGL, {
  Timeline,
  WeatherLayer,
  DataInspector,
  LegendControl,
  
} from '@aerisweather/react-mapsgl';
// import {AerisMapController} from '@aerisweather/mapsgl';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@aerisweather/mapsgl/dist/mapsgl.css';
import GradientLegend from './GradientLegend';
import LayerToggleRow from './LayerToggleRow';
import { useReduxStore } from '../hooks/useReduxStore';

import useIsFreePlan from '../hooks/useIsFreePlan';
// import useCurrentSubscription from '../hooks/useCurrentSubscription';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
// import { getPlanName } from '../utils/subscriptionUtils';
import {
  MAP_LAYER_CONTROLS,
  getLayerFeatureName,
} from '../constants/lockedFeatures';

// Mapbox setup
const Map = ReactMapboxGL({
  accessToken:
    'pk.eyJ1IjoiYm9jb2xlODIiLCJhIjoiY21mNDFjd2lyMDBxNDJqc2RvN3BkcDl0dSJ9.VyLeKZTBGiZOGc9NC9m3Ag',
});

// MapsGL setup
const MapController = ReactMapsGL({
  accessKeys: {
    id: 'bg0TphJbIuEYf3MndFXXc',
    secret: 'A4cm5jU6LgrueWPcxjg7crJY1mrt77ECV67dJGt8',
  },
});





// Default fallback: Minneapolis, MN (~44.9778° N, 93.2650° W) — used when no
// center is supplied and/or the user has not granted browser geolocation.
const DEFAULT_MAP_CENTER = { lat: 44.9778, lon: -93.265 };

const MapView = ({ center = DEFAULT_MAP_CENTER, zoom = 7 }) => {
  // const currentSubscription = useCurrentSubscription();
  // const planName = getPlanName(currentSubscription);
  // const isFreePlan = useIsFreePlan();
  // const { handleLockedClick } = useUpgradeModal();

  const { getState } = useReduxStore();
  const { dashBoardData } = getState('Home') || {};
  const currentSubscription = dashBoardData?.subscription;
  const planName = (currentSubscription?.planId?.name || 'Free').toLowerCase();
  const isFreePlan = useIsFreePlan();
  const { handleLockedClick } = useUpgradeModal();
  const [map, setMap] = useState('');
  const mapboxMapRef = useRef(null);
  const mapControllerRef = useRef();
  const isUnmountingRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState('temperatures');
  const [selectedChildLayer, setSelectedChildLayer] = useState('feels-like');
  const [showMobileLayers, setShowMobileLayers] = useState(false); // State for mobile layer popup

  // Map load handler
  const onMapLoad = (mapEl) => {
    if (isUnmountingRef.current) return;
    
    try {
      setMap(mapEl);
      mapboxMapRef.current = mapEl;
    } catch (error) {
      console.warn('Error in onMapLoad:', error);
    }
  };

  // Layer visibility state - only one layer active at a time
  const [layerVisibility, setLayerVisibility] = useState({
    temperature: true,
    radar: false,
    satellite: false,
    advisory: false,
    precipitation: false,
    wind: false,
    visibility: false,
    pressure: false,
    snowfall: false,
    forecast: false,
    roadCondition: false,
    observation: false
  });

  const allowedLayers = useMemo(() => {
    if (planName === 'free') return new Set(['temperature']);
    if (planName === 'starter') return new Set(['temperature', 'wind', 'radar', 'precipitation', 'satellite']);
    if (planName === 'pro' || planName === 'custom') return new Set(Object.keys(layerVisibility));
    return new Set(['temperature']);
  }, [currentSubscription]);

  const isLocked = (layerName) => !allowedLayers.has(layerName);

  useEffect(() => {
    // When plan changes, turn off disallowed layers and ensure at least one allowed is active
    setLayerVisibility((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (!allowedLayers.has(k)) next[k] = false;
      });
      const anyAllowedActive = Object.keys(next).some((k) => allowedLayers.has(k) && next[k]);
      if (!anyAllowedActive && allowedLayers.has('temperature')) {
        next.temperature = true;
      }
      return next;
    });
  }, [allowedLayers]);

  // Handle layer visibility changes - allow toggling on/off
  const handleLayerToggle = (layerName) => {
    if (isLocked(layerName)) {
      if (isFreePlan) {
        handleLockedClick(getLayerFeatureName(layerName));
      }
      return;
    }
    
    // mapControllerRef.current.source.add("feels-like")
    // if (isUnmountingRef.current) return;
    // if (layerName === 'advisory') return; // Disable advisory

    // If the layer is already active, turn it off
    if (layerVisibility[layerName]) {
      const newVisibility = {
        ...layerVisibility,
        [layerName]: false
      };
      setLayerVisibility(newVisibility);
      return;
    }

    // Turn off all layers and turn on the selected one
    const newVisibility = Object.keys(layerVisibility).reduce((acc, key) => {
      acc[key] = key === layerName;
      return acc;
    }, {});
    setLayerVisibility(newVisibility);
  };

  // Get active legend based on visible layers
  const getActiveLegend = () => {
    if (isUnmountingRef.current) return null;

    if (layerVisibility.temperature) {
      return {
        title: 'Temperature',
        description: 'Shows current temperature conditions. Blue indicates cold temperatures, red indicates hot temperatures.',
        colors: ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff8000', '#ff0000'],
        minLabel: '20°F',
        maxLabel: '80°F',
        minValue: 20,
        maxValue: 80,
        unit: '°F',
        icon: '🌡️',
        dataSource: 'AerisWeather Temperature Data'
      };
    }

    if (layerVisibility.radar) {
      return {
        title: 'Radar',
        description: 'Shows precipitation intensity.Blue indicates very light rain, Green indicates light rain,yellow indicates medium rain, red indicates heavy rain.',
        colors: ["#0080ee","#1c6b0b", '#ffff00',  '#ff0000', ],
        minLabel: '1 mm/h',
        maxLabel: '50+ mm/h',
        minValue: 1,
        maxValue: 50,
        unit: 'mm/h',
        icon: '📡',
        dataSource: 'AerisWeather Radar Data'
      };
    }

    if (layerVisibility.satellite) {
      return {
        title: 'Satellite',
        description: 'Shows cloud cover and atmospheric conditions. Dark indicates clear sky, white indicates dense clouds.',
        colors: ['#000000', '#404040', '#808080', '#c0c0c0', '#ffffff'],
        minLabel: 'Clear Sky',
        maxLabel: 'Dense Clouds',
        icon: '🛰️',
        dataSource: 'AerisWeather Satellite Data'
      };
    }

    if (layerVisibility.precipitation) {
      return {
        title: 'Precipitation',
        description: 'Precipitation: Light (light green), Medium (green), Heavy (dark green).',
        colors: ['#86efac', '#16a34a', '#14532d'],
        minLabel: '5 mm',
        maxLabel: '70+ mm',
        minValue: 5,
        maxValue: 70,
        unit: 'mm',
        icon: '🌧️',
        dataSource: 'AerisWeather Precipitation Data'
      };
    }

    if (layerVisibility.wind) {
      return {
        title: 'Wind',
        description: 'Shows wind speed and direction. Blue indicates calm winds, red indicates strong winds.',
        colors: ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff8000', '#ff0000'],
        minLabel: '5 mph',
        maxLabel: '20+ mph',
        minValue: 5,
        maxValue: 20,
        unit: 'mph',
        icon: '💨',
        dataSource: 'AerisWeather Wind Data'
      };
    }

    if (layerVisibility.visibility) {
      return {
        title: 'Visibility',
       description: 'Shows visibility conditions. Ligh Yellow indicates clear visibility, Dark Yellow indicates poor visibility.',
        colors: ['#ffb700', '#fee904', '#f8f8ca', ],
        minLabel: '1 km',
        maxLabel: '10+ km',
        minValue: 1,
        maxValue: 10,
        unit: 'km',
        icon: '👁️',
        dataSource: 'AerisWeather Visibility Data'
      };
    }

    if (layerVisibility.pressure) {
      return {
        title: 'Barometric Pressure',
        description: 'Shows atmospheric pressure. Red indicates low pressure, blue indicates high pressure.',
        colors: ['#ff0000', '#ff8000', '#ffff00', '#00ff00', '#00ffff', '#0000ff'],
        minLabel: '950 mb',
        maxLabel: '1050 mb',
        minValue: 950,
        maxValue: 1050,
        unit: 'mb',
        icon: '📊',
        dataSource: 'AerisWeather Pressure Data'
      };
    }

    if (layerVisibility.snowfall) {
      return {
        title: 'Snow Depth',
        description: 'Snow depth: Light (light blue), Moderate (dark blue), Heavy (pink).',
        colors: ["#71c6f5", '#1d4ed8', '#ec4899',"#fc7cbb"],
        minLabel: '5 in',
        maxLabel: '30+ in',
        minValue: 5,
        maxValue: 30,
        unit: 'in',
        icon: '❄️',
        dataSource: 'AerisWeather Snowfall Data'
      };
    }

    if (layerVisibility.roadCondition) {
      return {
        title: 'Road Conditions',
        description: 'Depicts a summary of road conditions based on severity, where green indicates dry roads with no issues, yellow indicates the potential for hazardous road conditions (wet, slick), and red indicates a high risk of adverse road conditions.',
        type: 'discrete',
        items: [
          { color: '#22c55e', label: 'Dry / No Issues' },
          { color: '#facc15', label: 'Potentially Hazardous (Wet, Slick)' },
          { color: '#ef4444', label: 'High Risk / Adverse' }
        ],
        icon: '🛣️',
        dataSource: 'AerisWeather Road Weather Summary'
      };
    }

    if (layerVisibility.forecast) {
      return {
        title: 'Forecast Points',
        description: 'Shows weather forecast data points. Orange indicates current conditions, purple indicates future predictions.',
        colors: ['#ff8000', '#ffcc00', '#ffff00', '#cc66ff', '#8000ff'],
        minLabel: 'Current',
        maxLabel: 'Future',
        icon: '🔮',
        dataSource: 'AerisWeather Forecast Data'
      };
    }

    if (layerVisibility.observation) {
      return {
        title: 'Observation',
        description: 'Combined observation overlays including feels-like temperature, visibility, and wind.',
        type: 'composite',
        legends: [
          {
            title: 'Feels-like',
            colors: ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff8000', '#ff0000'],
            minLabel: '20°F',
            maxLabel: '80°F',
            minValue: 20,
            maxValue: 80,
            unit: '°F'
          },
          {
            title: 'Visibility',
 description: 'Shows visibility conditions. Ligh Yellow indicates clear visibility, Dark Yellow indicates poor visibility.',
        colors: ['#ffb700', '#fee904', '#f8f8ca', ],
        minLabel: '1 km',
        maxLabel: '10+ km',
        minValue: 1,
        maxValue: 10,
            unit: 'km'
          },
          {
            title: 'Wind',
            colors: ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff8000', '#ff0000'],
            minLabel: '5 mph',
            maxLabel: '20+ mph',
            minValue: 5,
            maxValue: 20,
            unit: 'mph'
          }
        ],
        icon: '🌤️',
        dataSource: 'AerisWeather Observations'
      };
    }
    
    // Fallback legend when no layers are active
    return {
      title: 'Weather Map',
      description: 'Select a weather layer to view detailed information and legend.',
      colors: ['#404040', '#606060', '#808080', '#a0a0a0', '#c0c0c0'],
      minLabel: 'No Data',
      maxLabel: 'No Data',
      icon: '🌤️',
      dataSource: 'AerisWeather'
    };
  };

  // Enhanced cleanup with error handling and unmounting protection
  // useEffect(() => {
  //   return () => {
  //     isUnmountingRef.current = true;
      
  //     // Suppress MapsGL errors during unmounting
  //     const originalConsoleError = console.error;
  //     const originalConsoleWarn = console.warn;
      
  //     console.error = (...args) => {
  //       if (args[0] && typeof args[0] === 'string' && 
  //           (args[0].includes('mapsgl') || args[0].includes('trigger') || args[0].includes('WebGL'))) {
  //         return; // Suppress MapsGL-related errors during unmounting
  //       }
  //       originalConsoleError.apply(console, args);
  //     };
      
  //     console.warn = (...args) => {
  //       if (args[0] && typeof args[0] === 'string' && 
  //           (args[0].includes('mapsgl') || args[0].includes('trigger') || args[0].includes('WebGL'))) {
  //         return; // Suppress MapsGL-related warnings during unmounting
  //       }
  //       originalConsoleWarn.apply(console, args);
  //     };

  //     try {
  //       // Clean up MapController first
  //       if (mapControllerRef.current && typeof mapControllerRef.current.destroy === "function") {
  //         mapControllerRef.current.destroy();
  //         mapControllerRef.current = null;
  //       }
  //     } catch (error) {
  //       console.warn('Error destroying MapController:', error);
  //     }

  //     try {
  //       // Clean up Mapbox map
  //       if (mapboxMapRef.current && typeof mapboxMapRef.current.remove === "function") {
  //         mapboxMapRef.current.remove();
  //         mapboxMapRef.current = null;
  //       }
  //     } catch (error) {
  //       console.warn('Error removing Mapbox map:', error);
  //     }

  //     // Restore console methods
  //     setTimeout(() => {
  //       console.error = originalConsoleError;
  //       console.warn = originalConsoleWarn;
  //     }, 100);
  //   };
  // }, []);
  
useEffect(()=>{
  // if (mapboxMapRef.current) {
  //   const canvas = mapboxMapRef.current.getCanvas();
  //   const clone = canvas.cloneNode(true);
  //   canvas.parentNode.replaceChild(clone, canvas); // saare listeners wipe ho jate hain
  //   mapboxMapRef.current.remove();
  //   mapboxMapRef.current = null;
  // }
// return()=>{
//     setLayerVisibility({
//     temperature: false,
//     radar: false,
//     satellite: false,
//     advisory: false,
//     precipitation: false,
//     wind: false,
//     visibility: false,
//     pressure: false,
//     snowfall: false,
//     forecast: false
//   })
//   mapboxMapRef?.current?.remove()
//   mapboxMapRef?.current?.off()
//   mapboxMapRef?.current?.removeLayer("feels-like")
//   // const canvas = document.querySelector(".mapboxgl-canvas");
//   // if (canvas && canvas.parentNode) {
//   //   canvas.parentNode.removeChild(canvas);
//   // }
//   // mapsgl?.AerisMapController?.remove()

// }

},[])
  return (
    <div className="flex w-full flex-col space-y-4">
      {/* Mobile Trigger Row - Only visible on mobile */}
      <div 
        className="md:hidden flex items-center justify-between bg-[#20293F] p-4 rounded-lg border border-gray-500 cursor-pointer hover:bg-[#2a3655] transition-colors"
        onClick={() => setShowMobileLayers(true)}
      >
        <span className="text-white font-semibold">Map Layers</span>
        <span className="text-white text-xl">⚙️</span>
      </div>

      {/* Map and Controls Row */}
      <div className="flex w-full flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
        {/* Left side: Map display and Legend */}
        <div className="w-full md:w-3/4 flex flex-col space-y-4">
          {/* MapsGL Map */}
          <div style={{ width: '100%', height: '70vh', borderRadius: '20px', border: '1px solid #374151' }}>
            <Map
              center={[center.lon, center.lat]}
              zoom={[zoom]}
              onStyleLoad={onMapLoad}
              style="mapbox://styles/mapbox/light-v9"
              containerStyle={{
                width: '100%',
                height: '100%',
                borderRadius: '20px',
              }}
            >
              {map && (
                <MapController
                
                onLoad={(controller)=>{
                }}
                onLoadComplete={(controller)=>{
                  mapControllerRef.current = controller
                }}
                onLayerAdd={()=>{
                }}
                onLayerRemove={()=>{
                }}
                  // ref={mapControllerRef}
                 
                  onError={(error) => {
                    // if (!isUnmountingRef.current) {
                    //   console.warn('MapController error:', error);
                    // }
                  }}
                  strategy="mapbox" 
                  map={map}
                >
                  <Timeline
                    start={new Date(Date.now() - 3600 * 12 * 1000)}
                    duration={6}
                    isPlaying={playing}
                    onPlay={() => {
                    }}
                    onStop={() => {
                    }}
                  />
                  <DataInspector event="move" />
              <LegendControl
                width={typeof window !== 'undefined' && window.innerWidth < 768 ? 320 : 800}
                toggleOnClick={true}
              />
           
                  {/* Dynamic Weather Layer - Only show the active layer */}
                         {layerVisibility.observation && <WeatherLayer key="humidity" id="humidity" />}

                  {layerVisibility.observation&& <WeatherLayer  key="observation" id="feels-like" />}
                  {layerVisibility.temperature&& <WeatherLayer  key="feels-like" id="feels-like" />}
                  {layerVisibility.radar && <WeatherLayer key="radar" id="radar" />}
                  {layerVisibility.satellite && <WeatherLayer key="satellite" id="satellite" />}
                  {layerVisibility.precipitation && <WeatherLayer key="precip-accum" id="precip-accum" />}
                  {(layerVisibility.wind||layerVisibility.observation) && <WeatherLayer key="wind-particles" id="wind-particles" />}
                  {(layerVisibility.visibility||layerVisibility?.observation) && <WeatherLayer key="visibility" id="visibility" />}
                  {layerVisibility.pressure && <WeatherLayer key="pressure-msl-contour" id="pressure-msl-contour" />}
                  {layerVisibility.snowfall && <WeatherLayer key="snow-depth" id="snow-depth" />}
                  {layerVisibility.roadCondition && <WeatherLayer key="road-weather-summary-us" id="road-weather-summary-us" />}
                  {/* {layerVisibility.roadCondition && <WeatherLayer key="road-weather-summary-us" id="froad-weather-risk-hydroplane-us" />} */}
                  {/* {layerVisibility.roadCondition && <WeatherLayer key="road-weather-summary-us" id="froad-weather-risk-hydroplane-us" />} */}
                  {/* {layerVisibility.roadCondition && <WeatherLayer key="road-weather-summary-us" id="road-weather-risk-low-viz-fog-us" />} */}

                  
                  {/* Always show these base layers */}
                  <WeatherLayer  id="place-city" />
                  <WeatherLayer id="admin-3-4-boundaries" />
                  <WeatherLayer id="admin-2-boundaries" />
                  {(layerVisibility.temperature) && <WeatherLayer key="temperatures-text" id="temperatures-text" />}
                  {(layerVisibility.observation) && <WeatherLayer key="observation-text" id="temperatures-text" />}
                </MapController>
              )}
            </Map>
          </div>

          {/* Legend below the map */}
          <div className="flex flex-col border border-gray-600 bg-[#20293F] py-3 px-3 rounded-lg shadow-sm space-y-2">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <span className="text-base md:text-lg">{getActiveLegend().icon}</span>
                <span className="text-white font-semibold text-base md:text-lg">{getActiveLegend().title}</span>
              </div>
              {/* <span className="text-gray-400 text-xs">Data Source: {getActiveLegend().dataSource}</span> */}
            </div>
            
            
            <div className="flex items-center justify-between">
              {getActiveLegend()?.type === 'composite' && Array.isArray(getActiveLegend()?.legends) ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full">
                  {getActiveLegend().legends.map((l) => (
                    <div key={l.title} className="w-full">
                      <div className="mb-1">
                        <span className="text-[11px] md:text-xs text-white">{l.title}</span>
                      </div>
                      <GradientLegend height="h-3" colors={l.colors} minLabel={l.minLabel} maxLabel={l.maxLabel} minValue={l.minValue} maxValue={l.maxValue} unit={l.unit} />
                    </div>
                  ))}
                </div>
              ) : getActiveLegend()?.type === 'discrete' && Array.isArray(getActiveLegend()?.items) ? (
                <div className="flex flex-wrap gap-4 w-full">
                  {getActiveLegend().items.map((item) => (
                    <div key={item.label} className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ background: item.color }}></span>
                      <span className="text-[11px] md:text-xs text-white">{item.label}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <GradientLegend
                  height="h-3"
                  colors={getActiveLegend().colors}
                  minLabel={getActiveLegend().minLabel}
                  maxLabel={getActiveLegend().maxLabel}
                  minValue={getActiveLegend().minValue}
                  maxValue={getActiveLegend().maxValue}
                  unit={getActiveLegend().unit}
                />
              )}
            </div>
            <p className="text-gray-400 text-[11px] md:text-xs">{getActiveLegend().description}</p>

          </div>
        </div>

        {/* Right side: Layer controls */}
        <div className="w-full md:w-1/4 flex flex-col space-y-4">
          
          {/* Weather Points Data */}
          <div className={`
            flex-col border border-gray-500 bg-[#20293F] py-4 px-4 shadow-md space-y-4
            ${showMobileLayers 
              ? 'fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl rounded-b-none h-[80vh] flex border-b-0' 
              : 'hidden'}
            md:flex md:static md:h-[70vh] md:w-full md:rounded-lg md:z-auto md:border
          `}>
            {/* Mobile Popup Header */}
            <div className="md:hidden flex justify-between items-center mb-2 pb-2 border-b border-gray-600">
               <h3 className="text-white font-semibold text-lg">Weather Points Data</h3>
               <button 
                 onClick={(e) => { e.stopPropagation(); setShowMobileLayers(false); }}
                 className="text-gray-400 hover:text-white text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-700"
               >
                 ×
               </button>
             </div>

            <h3 className="hidden md:block text-white font-semibold text-lg md:text-xl mb-2">Weather Points Data</h3>
            
            {/* Layer Controls with Toggle Switches */}
            <div className="space-y-3 overflow-y-auto flex-1">
              {MAP_LAYER_CONTROLS.map(({ key, icon, label, featureName }) => (
                <LayerToggleRow
                  key={key}
                  icon={icon}
                  label={label}
                  featureName={featureName}
                  locked={isLocked(key)}
                  checked={!!layerVisibility[key]}
                  onToggle={() => handleLayerToggle(key)}
                  onLockedClick={handleLockedClick}
                />
              ))}
            
            </div>
          </div>

          {/* Timeline Controls */}
          <div className="flex flex-col border border-gray-500 bg-[#20293F] py-4 px-4 rounded-lg shadow-md space-y-4">
            <h3 className="text-white font-semibold text-xl mb-2">Timeline Controls</h3>
            
            <div className="flex items-center justify-between">
              <span className="text-white text-sm">Play/Pause</span>
              <button
                onClick={() => {
                  if (!isUnmountingRef.current) {
                    setPlaying(!playing);
                  }
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  playing 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {playing ? '⏸️ Pause' : '▶️ Play'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapView;



